import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Image,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { Link, useNavigate } from "@remix-run/react";
import {
  IconBriefcase,
  IconCalendar,
  IconClock,
  IconShare,
} from "@tabler/icons-react";

import DefaultImage from "~/assets/misc/masterclass.webp";
import { Masterclass } from "~/types/masterclass";
import { formatCurrency } from "~/utils/format-currency";

import classes from "./masterclass-card.module.scss";

interface MasterclassCardProps {
  masterclass: Masterclass;
  locale: string;
  currency: string;
  enrollLabel: string;
  shareLabel: string;
  href: string;
  specialistHref?: string;
  specialistLabel?: string;
  onShare?: (masterclass: Masterclass) => void;
}

export const MasterclassCard = ({
  masterclass,
  locale,
  currency,
  enrollLabel,
  shareLabel,
  href,
  specialistHref,
  specialistLabel,
  onShare,
}: MasterclassCardProps) => {
  const navigate = useNavigate();
  const date = new Date(masterclass.dateTime);
  const hasValidDate = !Number.isNaN(date.getTime());
  const isPast = hasValidDate && date.getTime() < Date.now();
  const canEnroll = !isPast;

  return (
    <article
      className={`${classes.card} ${isPast ? classes.past : ""}`}
      tabIndex={canEnroll ? 0 : undefined}
      role={canEnroll ? "link" : undefined}
      aria-disabled={isPast || undefined}
      onClick={() => {
        if (canEnroll) navigate(href);
      }}
      onKeyDown={event => {
        if (canEnroll && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          navigate(href);
        }
      }}>
      <Image
        src={masterclass.banner ?? DefaultImage}
        alt={masterclass.name}
        className={classes.image}
        height={170}
      />

      <Stack gap="sm" className={classes.content}>
        <Stack gap={4}>
          <Group justify="space-between" align="start" gap="xs" wrap="nowrap">
            <Title order={3} fz={18} fw={600} lineClamp={2}>
              {masterclass.name}
            </Title>
            {isPast ? (
              <Badge color="gray" variant="light" className={classes.badge}>
                {locale === "fr" ? "Terminée" : "Ended"}
              </Badge>
            ) : null}
          </Group>
          {masterclass.description ? (
            <Text c="dimmed" fz={13} lineClamp={2}>
              {masterclass.description}
            </Text>
          ) : null}
        </Stack>

        <Stack gap={6}>
          {masterclass.providerDetails?.fullName ? (
            specialistHref ? (
              <Link
                to={specialistHref}
                className={classes.specialist}
                onClick={event => event.stopPropagation()}>
                <Group gap={6} wrap="nowrap">
                  <IconBriefcase size={16} stroke={1.5} />
                  <Text fz={13} c="dimmed" lineClamp={1}>
                    {masterclass.providerDetails.fullName}
                    {masterclass.providerDetails.specialization
                      ? `, ${masterclass.providerDetails.specialization}`
                      : ""}
                  </Text>
                </Group>
              </Link>
            ) : (
              <Group gap={6} wrap="nowrap">
                <IconBriefcase size={16} stroke={1.5} />
                <Text fz={13} c="dimmed" lineClamp={1}>
                  {masterclass.providerDetails.fullName}
                  {masterclass.providerDetails.specialization
                    ? `, ${masterclass.providerDetails.specialization}`
                    : ""}
                </Text>
              </Group>
            )
          ) : null}

          {hasValidDate ? (
            <>
              <Group gap={6} wrap="nowrap">
                <IconCalendar size={16} stroke={1.5} />
                <Text fz={13}>
                  {date.toLocaleDateString(locale, { dateStyle: "medium" })}
                </Text>
              </Group>
              <Group gap={6} wrap="nowrap">
                <IconClock size={16} stroke={1.5} />
                <Text fz={13}>
                  {date.toLocaleTimeString(locale, { timeStyle: "short" })}
                </Text>
              </Group>
            </>
          ) : null}
        </Stack>

        <Group justify="space-between" align="end" mt="auto">
          <Stack gap={2}>
            <Text fz={12} c="dimmed">
              {locale === "fr" ? "Prix" : "Price"}
            </Text>
            <Text fw={700}>
              {formatCurrency(masterclass.price, locale, currency)}
            </Text>
          </Stack>

          <Group gap="xs">
            {specialistHref && specialistLabel ? (
              <Button
                component={Link}
                to={specialistHref}
                variant="subtle"
                radius="xl"
                size="compact-sm"
                onClick={event => event.stopPropagation()}>
                {specialistLabel}
              </Button>
            ) : null}
            {canEnroll ? (
              <Button
                component={Link}
                to={href}
                className={classes.enroll}
                radius="xl"
                size="compact-sm"
                onClick={event => event.stopPropagation()}>
                {enrollLabel}
              </Button>
            ) : (
              <Button
                className={classes.enroll}
                radius="xl"
                size="compact-sm"
                disabled
                onClick={event => event.stopPropagation()}>
                {locale === "fr" ? "Indisponible" : "Unavailable"}
              </Button>
            )}
            {onShare ? (
              <ActionIcon
                variant="outline"
                radius="xl"
                aria-label={shareLabel}
                title={shareLabel}
                onClick={event => {
                  event.stopPropagation();
                  onShare(masterclass);
                }}>
                <IconShare size={16} stroke={1.5} />
              </ActionIcon>
            ) : null}
          </Group>
        </Group>
      </Stack>
    </article>
  );
};
